<template>
  <page-header title="Data Center" :identifier="id">
    <template v-if="isLoaded">
      <button v-if="dataCenter.id" class="btn-outline-primary ml-auto h-10 mr-2" @click="toggleDataCenter">
        <template v-if="dataCenter.isEnabled">
          <EyeSlashIcon class="size-5 -ml-1 mr-2" />
          Disable
        </template>
        <template v-else>
          <EyeIcon class="size-5 -ml-1 mr-2" />
          Enable
        </template>
      </button>
      <button class="btn-primary ml-auto h-10" :class="{'disabled': isSaving || !isDataChanged || result.$invalid }" @click="save">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Save
      </button>
    </template>
  </page-header>

  <template v-if="isLoaded">
    <form @submit.prevent="save">
      <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <form-input v-model="dataCenter.name" label="Name" class="md:col-span-6" :error-msg="result.name.$messages.join(' | ')" />

        <form-input v-model="dataCenter.country" label="Country" class="md:col-span-3" :error-msg="result.country.$messages.join(' | ')" />

        <form-input v-model="dataCenter.city" label="City" class="md:col-span-3" :error-msg="result.city.$messages.join(' | ')" />

        <form-input v-model="dataCenter.latitude" label="Latitude" class="md:col-span-3" :error-msg="result.latitude.$messages.join(' | ')" />

        <form-input v-model="dataCenter.longitude" label="Longitude" class="md:col-span-3" :error-msg="result.longitude.$messages.join(' | ')" />
      </div>
    </form>

    <data-center-clusters
      v-if="dataCenter.id"
      ref="clustersSectionRef"
      :data-center-id="dataCenter.id"
    />
  </template>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { useToast } from 'vue-toastification';
import { Modal } from '../composables/useModal';
import hashSum from 'hash-sum';
import {
  CircleStackIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/vue/24/outline';
import PageHeader from '../components/PageHeader.vue';
import FormInput from '../components/FormInput.vue';
import DataCenterClusters from '../components/DataCenterClusters.vue';
import useValidate from 'vue-tiny-validate';

const props = defineProps({
  id: {
    type: String,
    default: null
  }
});

const toast = useToast();
const isLoaded = ref(false);
const isSaving = ref(false);
const dataCenter = ref({});
const dataCenterHashSum = ref(null);

const isDataChanged = computed(() => dataCenterHashSum.value !== hashSum(dataCenter.value));

if (props.id) {
  load();
} else {
  isLoaded.value = true;
}

const rgxCheckCoordinate = rgx => value => !value || rgx.test(value);
const rules = reactive({
  name: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Name must not be empty.'
  },
  country: {
    name: 'required',
    test: value => Boolean(value),
    message: 'Country must not be empty.'
  },
  city: {
    name: 'required',
    test: value => Boolean(value),
    message: 'City must not be empty.'
  },
  latitude: {
    name: 'required',
    test: rgxCheckCoordinate(/^-?\d+(\.\d+)?$/),
    message: 'Latitude must be a number.'
  },
  longitude: {
    name: 'required',
    test: rgxCheckCoordinate(/^-?\d+(\.\d+)?$/),
    message: 'Longitude must be a number.'
  }
});
const options = reactive({
  autoTest: true
});
const { result } = useValidate(dataCenter, rules, options);

const clustersSectionRef = ref(null);

async function load() {
  const { data, error } = await useFetch(`${apiDomain}/api/data-center/${props.id}`).json().get();

  if (error.value) {
    toast.error('Error loading data center');
    return;
  }

  dataCenter.value = data.value;
  dataCenterHashSum.value = hashSum(dataCenter.value);
  isLoaded.value = true;
}

async function validate() {
  return await result.value.$test();
}

async function save() {
  isSaving.value = true;

  await validate();
  if (result.value.$invalid) {
    isSaving.value = false;
    return;
  }

  const { error } = await useFetch(`${apiDomain}/api/data-center/`).post(dataCenter.value).json();

  if (error.value) {
    toast.error('Error saving data center');
    isSaving.value = false;
    return;
  }

  dataCenterHashSum.value = hashSum(dataCenter.value);
  isSaving.value = false;
  toast.success('Data center saved successfully');

  // Reload clusters if they are visible and the component is mounted
  if (dataCenter.value.id && clustersSectionRef.value) {
    clustersSectionRef.value.reloadGrig();
  }
}

async function toggleDataCenter() {
  const direction = dataCenter.value.isEnabled ? 'disable' : 'enable';
  const { data } = await useFetch(`${apiDomain}/api/data-center/${direction}/${dataCenter.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success(`Data Center ${direction}d successfully`);
    return;
  }

  toast.error(`Error ${direction} Data Center`);
}

function handleBeforeUnload(e) {
  if (!isDataChanged.value || !Object.keys(dataCenter.value).length) {
    return;
  }
  e.preventDefault();
  e.returnValue = '';
}

onBeforeRouteLeave(async (to, from, next) => {
  if (!isDataChanged.value || !Object.keys(dataCenter.value).length) {
    return next();
  }

  const confirm = await Modal.confirm({
    title: 'Unsaved changes',
    body: 'You have unsaved changes. <br/> If you leave this page, your changes will be lost. <br/> Are you sure you want to exit without saving?',
    okButton: 'Leave',
    iconColor: 'primary'
  });

  return confirm ? next() : next(false);
});

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
